import {
  BookingParty,
  BuyersConsolidationQuoteServicesMst,
  GenericLocationType,
  getDestinationPortType,
  getOriginPortType,
  IQuoteRequest,
  TBuyersConsolidationQuoteServicesMst,
} from 'features/BookingFlow'
import { Address, PlacesSearchType, placeToString, PortOrAddress, PortType, TLegalEntity } from 'features/Directory'
import { IContact, ShipmentMode } from 'features/ShipmentDetails'
import { History } from 'history'
import { includes } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { getSnapshot } from 'mobx-state-tree'
import { isMoment, Moment } from 'moment'
import { API } from 'service/API'
import { AuthStore } from 'service/Auth'
import { isEuropeCountryCode, ISO } from 'service/ISO'

import { ManualQuoteAdditionalFieldsModel, TManualQuoteAdditionalFieldsModel } from './ManualQuoteAdditionalFieldsModel'

const PortTypes: PortType[] = ['air', 'sea', 'rail']
type ManualQuoteType = 'HEAVY_CARGO' | 'DANGEROUS_GOODS' | 'INVALID_PORT' | 'default' | 'customMode' | 'INVALID_REQUEST'
type DialogStatus = 'overview' | 'sending' | 'sent' | 'inprogress'

const CUSTOMER_NOTES_DANGEROUS_GOODS_MIN_LENGTH = 20

export class ManualQuoteStore {
  API: API
  history: History
  authStore: AuthStore
  // @ts-expect-error TS(2564) FIXME: Property 'quoteSheetId' has no initializer and is ... Remove this comment to see the full error message
  quoteSheetId: string
  // @ts-expect-error TS(2564) FIXME: Property 'promise' has no initializer and is not d... Remove this comment to see the full error message
  promise: Promise<boolean>

  @observable
  shown = false
  @observable
  status: DialogStatus = 'overview'
  @observable
  customerNotes: string = ''
  @observable
  // @ts-expect-error TS(2564) FIXME: Property 'type' has no initializer and is not defi... Remove this comment to see the full error message
  type: ManualQuoteType
  @observable
  // @ts-expect-error TS(2564) FIXME: Property 'pickupAddress' has no initializer and is... Remove this comment to see the full error message
  pickupAddress: string
  @observable
  // @ts-expect-error TS(2564) FIXME: Property 'pickupLocation' has no initializer and i... Remove this comment to see the full error message
  pickupLocation: GenericLocationType
  @observable
  // @ts-expect-error TS(2564) FIXME: Property 'deliveryAddress' has no initializer and ... Remove this comment to see the full error message
  deliveryAddress: string
  @observable
  // @ts-expect-error TS(2564) FIXME: Property 'deliveryLocation' has no initializer and... Remove this comment to see the full error message
  deliveryLocation: GenericLocationType
  @observable
  shipper?: IContact
  @observable
  legalEntity?: TLegalEntity
  @observable
  // @ts-expect-error TS(2564) FIXME: Property 'cargoReadyAt' has no initializer and is ... Remove this comment to see the full error message
  cargoReadyAt: Date
  additionalRequirements: TManualQuoteAdditionalFieldsModel = ManualQuoteAdditionalFieldsModel.create({})
  buyersConsolidation: TBuyersConsolidationQuoteServicesMst | undefined
  @observable
  incoTerm: ISO.IncoTermsCode | undefined
  @observable
  bookingParty: BookingParty | undefined
  // @ts-expect-error TS(2564) FIXME: Property 'resolve' has no initializer and is not d... Remove this comment to see the full error message
  private resolve: (bool: boolean) => void
  // @ts-expect-error TS(2564) FIXME: Property 'reject' has no initializer and is not de... Remove this comment to see the full error message
  private reject: (bool: boolean) => void
  @observable
  // @ts-expect-error TS(2564) FIXME: Property '_quoteRequest' has no initializer and is... Remove this comment to see the full error message
  private _quoteRequest: IQuoteRequest

  constructor(API: API, history: History, authStore: AuthStore) {
    this.API = API
    this.history = history
    this.authStore = authStore
  }

  @computed
  get quoteRequest() {
    return this._quoteRequest
  }

  @computed
  get dangerousGoods() {
    return this.type === 'DANGEROUS_GOODS'
  }

  @computed
  get isExportBooking() {
    return isEuropeCountryCode(this._quoteRequest?.origin.countryCode)
  }

  @computed
  get heavyCargo() {
    return this.type === 'HEAVY_CARGO'
  }

  @computed
  get isOriginInvalidAddress(): boolean {
    if (!this._quoteRequest) return false

    return this.isAddress(this._quoteRequest.origin) && !this.isValidAddress(this._quoteRequest.origin)
  }

  @computed
  get isDestinationInvalidAddress(): boolean {
    if (!this._quoteRequest) return false

    return this.isAddress(this._quoteRequest.destination) && !this.isValidAddress(this._quoteRequest.destination)
  }

  @computed
  get disabled(): boolean {
    return (
      (this.buyersConsolidation && !this.buyersConsolidation.isValid) ||
      this.customerNoteMinLengthRemaining > 0 ||
      !this.cargoReadyAt ||
      (this.isOriginInvalidAddress && !this.pickupAddress) ||
      (this.isDestinationInvalidAddress && !this.deliveryAddress) ||
      !this.incoTerm
    )
  }

  @computed
  get customerNoteMinLengthRemaining(): number {
    if (!this.dangerousGoods) {
      return 0
    }

    return this.customerNotes
      ? Math.max(CUSTOMER_NOTES_DANGEROUS_GOODS_MIN_LENGTH - this.customerNotes.trim().length, 0)
      : CUSTOMER_NOTES_DANGEROUS_GOODS_MIN_LENGTH
  }

  @computed
  get pickupTypes(): PlacesSearchType[] {
    return ['address', ...this.quoteRequest.modes.map(getOriginPortType)]
  }

  @computed
  get deliveryTypes(): PlacesSearchType[] {
    if (this.isMode('rail') && this.quoteRequest.type === 'fcl') {
      return ['address']
    }
    return ['address', ...this.quoteRequest.modes.map(getDestinationPortType)]
  }

  @action
  setPickupAddress(address: string): void {
    this.pickupAddress = address
  }

  @action
  setDeliveryAddress(address: string): void {
    this.deliveryAddress = address
  }

  @action
  setPickupLocation(location: GenericLocationType): void {
    this.pickupLocation = location
  }

  @action
  setDeliveryLocation(location: GenericLocationType): void {
    this.deliveryLocation = location
  }

  @action
  setShipper(shipper: IContact): void {
    this.shipper = shipper
  }

  @action
  setLegalEntity(legalEntity: TLegalEntity): void {
    this.legalEntity = legalEntity
  }

  @action
  setCargoReadyAt = (date: Date | Moment): void => {
    this.cargoReadyAt = isMoment(date) ? date.toDate() : date
  }

  @action
  setIncoTerm = (incoTerm: ISO.IncoTermsCode) => {
    this.quoteRequest.incoTerm = incoTerm
    this.incoTerm = incoTerm
  }

  @action
  setBookingParty = (bookingParty: BookingParty) => {
    this.bookingParty = bookingParty
  }

  /**
   * @param quoteRequest
   * @param id
   * @param type
   * @return {Promise<boolean>} returns a promise that resolves to true when request is sent and
   *                            false when it is closed
   */
  @action
  triggerManualDialog(quoteRequest: IQuoteRequest, id?: string, type?: ManualQuoteType): Promise<boolean> {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve
      this.reject = reject
    })

    this.shown = true
    this._quoteRequest = quoteRequest
    // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    this.quoteSheetId = id
    // @ts-expect-error TS(2322) FIXME: Type 'Date | null' is not assignable to type 'Date... Remove this comment to see the full error message
    this.cargoReadyAt = quoteRequest.cargoReadyAt && new Date(quoteRequest.cargoReadyAt)
    // @ts-expect-error TS(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    this.setPickupAddress(placeToString(quoteRequest.origin))
    this.setPickupLocation(quoteRequest.origin)
    // @ts-expect-error TS(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    this.setDeliveryAddress(placeToString(quoteRequest.destination))
    this.setDeliveryLocation(quoteRequest.destination)
    this.incoTerm = quoteRequest.incoTerm
    // @ts-expect-error TS(2345) FIXME: Argument of type 'BookingParty | undefined' is not... Remove this comment to see the full error message
    this.setBookingParty(quoteRequest.bookingParty)

    if (quoteRequest.buyersConsolidation) {
      this.buyersConsolidation = BuyersConsolidationQuoteServicesMst.create(quoteRequest.buyersConsolidation)
      this.buyersConsolidation.setPristine()
    }

    if (type) {
      this.type = type
    } else if (quoteRequest.dangerousGoods) {
      this.type = 'DANGEROUS_GOODS'
    } else {
      this.type = 'default'
    }
    return this.promise
  }

  @action
  closeWithoutReset() {
    this.shown = false
  }

  @action
  close() {
    if (this.status === 'sent') {
      this.resolve(true)
    } else {
      this.resolve(false)
    }
    this.shown = false
    this.status = 'overview'
    this.customerNotes = ''
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'ManualQuote... Remove this comment to see the full error message
    this.type = null
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'string'.
    this.pickupAddress = null
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'string'.
    this.deliveryAddress = null
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'Date'.
    this.cargoReadyAt = null
    this.additionalRequirements = ManualQuoteAdditionalFieldsModel.create({})
    this.buyersConsolidation = undefined
  }

  @action
  requestManualQuote() {
    this.status = 'sending'
    const {
      quoteRequest,
      quoteSheetId,
      customerNotes,
      pickupAddress,
      deliveryAddress,
      cargoReadyAt,
      additionalRequirements,
      buyersConsolidation,
      bookingParty,
    } = this

    return this.API.quoteSheets
      .requestManualQuote({
        modes: quoteRequest.modes,
        quoteSheetId,
        customerNotes,
        pickupAddress,
        deliveryAddress,
        cargoReadyAt,
        additionalRequirements: additionalRequirements.JSON,
        buyersConsolidation: buyersConsolidation ? getSnapshot(buyersConsolidation) : undefined,
        incoterm: this.incoTerm,
        bookingParty,
      })
      .then((manualQuoteResponse) => {
        this.status = 'sent'
        this.history.push('/quotes')
        return manualQuoteResponse
      })
      .catch(() => this.close())
  }

  isAddress(portOrAddress: PortOrAddress): portOrAddress is Address {
    return (portOrAddress?.types as PortType[])
      ?.map((type) => includes(PortTypes, type))
      ?.some((isPort) => isPort === false)
  }

  private isValidAddress(address: Address): boolean {
    const { googplace_types, street, streetNumber } = address
    const isValidType = googplace_types && googplace_types.some((type) => !!type.match(/street_address|premise|route/))
    const hasStreetAddressFields = !!street && !!streetNumber

    return isValidType || hasStreetAddressFields
  }

  private isMode(mode: ShipmentMode): boolean {
    return this.quoteRequest.modes.length === 1 && this.quoteRequest.modes[0] === mode
  }
}
